import { useState, useMemo, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragStartEvent, DragEndEvent } from '@dnd-kit/core';
import { useDroppable } from '@dnd-kit/core';
import { useDraggable } from '@dnd-kit/core';
import { MessageSquare, Ban } from 'lucide-react';
import type { IssueListItem } from '../../services/issueService';
import type { WorkflowStatusInfo } from '../../services/projectService';
import type { WorkflowTransitionsData } from '../../services/workflowService';
import { PRIORITY_BADGE } from './IssueListView';
import { resolveIssueTypeConfig } from '../../constants/issueTypeConfig';
import {
  buildDynamicColumns,
  buildCategoryColumns,
  resolveTargetStatusId,
  getClientAllowedTargets,
  getClientAllowedCategoryTargets,
  type StatusCategory,
  type ProjectRole,
} from '../board/boardUtils';
import toast from 'react-hot-toast';
import Avatar from '../common/Avatar';
import { useProfileModal } from '../common/ProfileModalProvider';
import type { ProfileColorKey } from '@maios/shared';

interface WorkflowGroupInfo {
  workflowId: string;
  workflowName: string;
  statuses: WorkflowStatusInfo[];
}

interface IssueTypeWorkflowMapping {
  issueType: string;
  workflow: { workflowId: string };
}

interface IssueBoardViewProps {
  items: IssueListItem[];
  workflowStatuses: WorkflowStatusInfo[];
  isMultiWorkflow?: boolean;
  workflowGroups?: WorkflowGroupInfo[];
  issueTypeWorkflows?: IssueTypeWorkflowMapping[];
  defaultWorkflowId?: string;
  onTransition: (issueId: string, newStatusId: string) => Promise<void>;
  onIssueClick: (issue: IssueListItem) => void;
  readOnly?: boolean;
  // CHG-043: Transition rules
  transitionDataMap?: Map<string, WorkflowTransitionsData>;
  userProjectRole?: ProjectRole | null;
  isSystemAdmin?: boolean;
}

export default function IssueBoardView({
  items,
  workflowStatuses,
  isMultiWorkflow = false,
  workflowGroups = [],
  issueTypeWorkflows = [],
  defaultWorkflowId = '',
  onTransition,
  onIssueClick,
  readOnly = false,
  transitionDataMap,
  userProjectRole = null,
  isSystemAdmin = false,
}: IssueBoardViewProps) {
  const [activeIssue, setActiveIssue] = useState<IssueListItem | null>(null);
  // CHG-043: allowed target status IDs during drag
  const [allowedTargets, setAllowedTargets] = useState<Map<string, { isAllowed: boolean; disabledReason: string | null }> | null>(null);

  const statusColumns = useMemo(() => buildDynamicColumns(workflowStatuses), [workflowStatuses]);
  const categoryColumns = useMemo(() => buildCategoryColumns(), []);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: readOnly ? Infinity : 8 },
    }),
  );

  // CHG-043: Resolve the transition data for a given issue
  const getTransitionData = useCallback(
    (issue: IssueListItem): WorkflowTransitionsData | null => {
      if (!transitionDataMap) return null;
      // Find the effective workflow for this issue
      const override = issueTypeWorkflows.find((m) => m.issueType === issue.issueType);
      const effectiveWorkflowId = override?.workflow.workflowId ?? defaultWorkflowId;
      return transitionDataMap.get(effectiveWorkflowId) ?? null;
    },
    [transitionDataMap, issueTypeWorkflows, defaultWorkflowId],
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const issue = items.find((i) => i.id === event.active.id);
      setActiveIssue(issue ?? null);

      // CHG-043: Compute allowed targets
      if (issue && transitionDataMap) {
        const tData = getTransitionData(issue);
        if (tData) {
          if (isMultiWorkflow) {
            // For multi-workflow: compute per category
            const targets = new Map<string, { isAllowed: boolean; disabledReason: string | null }>();
            for (const cat of ['TODO', 'IN_PROGRESS', 'DONE'] as StatusCategory[]) {
              if (cat === issue.workflowStatus.category) continue;
              const result = getClientAllowedCategoryTargets(
                issue.statusId,
                cat,
                tData,
                userProjectRole,
                isSystemAdmin,
              );
              targets.set(cat, { isAllowed: result.isAllowed, disabledReason: result.disabledReason });
            }
            setAllowedTargets(targets);
          } else {
            // For single workflow: compute per status
            const targets = getClientAllowedTargets(issue.statusId, tData, userProjectRole, isSystemAdmin);
            setAllowedTargets(targets);
          }
        } else {
          setAllowedTargets(null);
        }
      } else {
        setAllowedTargets(null);
      }
    },
    [items, transitionDataMap, getTransitionData, isMultiWorkflow, userProjectRole, isSystemAdmin],
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      setActiveIssue(null);
      setAllowedTargets(null);

      const { active, over } = event;
      if (!over) return;

      const issueId = active.id as string;
      const issue = items.find((i) => i.id === issueId);
      if (!issue) return;

      if (isMultiWorkflow) {
        const targetCategory = over.id as StatusCategory;
        if (issue.workflowStatus.category === targetCategory) return;

        // CHG-043: Check transition rules
        if (transitionDataMap) {
          const tData = getTransitionData(issue);
          if (tData && tData.transitionMode === 'RESTRICTED') {
            const result = getClientAllowedCategoryTargets(
              issue.statusId,
              targetCategory,
              tData,
              userProjectRole,
              isSystemAdmin,
            );
            if (!result.isAllowed) {
              toast.error(result.disabledReason ?? '현재 상태에서 해당 카테고리로 전환할 수 없습니다');
              return;
            }
            if (result.targetStatusId) {
              try {
                await onTransition(issueId, result.targetStatusId);
              } catch {
                toast.error('상태 전이에 실패했습니다.');
              }
              return;
            }
          }
        }

        // Fallback: original behavior
        const targetStatusId = resolveTargetStatusId(
          issue,
          targetCategory,
          workflowGroups,
          issueTypeWorkflows,
          defaultWorkflowId,
        );
        if (!targetStatusId) {
          toast.error('대상 카테고리에 해당하는 상태를 찾을 수 없습니다.');
          return;
        }
        try {
          await onTransition(issueId, targetStatusId);
        } catch {
          toast.error('상태 전이에 실패했습니다.');
        }
      } else {
        const targetStatusId = over.id as string;
        if (issue.statusId === targetStatusId) return;

        // CHG-043: Check transition rules
        if (transitionDataMap) {
          const tData = getTransitionData(issue);
          if (tData && tData.transitionMode === 'RESTRICTED') {
            const allowedMap = getClientAllowedTargets(issue.statusId, tData, userProjectRole, isSystemAdmin);
            const target = allowedMap.get(targetStatusId);
            if (!target) {
              toast.error(`현재 상태에서 이 상태로 전환할 수 없습니다`);
              return;
            }
            if (!target.isAllowed) {
              toast.error(target.disabledReason ?? '전환 권한이 없습니다');
              return;
            }
          }
        }

        try {
          await onTransition(issueId, targetStatusId);
        } catch {
          toast.error('상태 전이에 실패했습니다.');
        }
      }
    },
    [items, isMultiWorkflow, transitionDataMap, getTransitionData, userProjectRole, isSystemAdmin, workflowGroups, issueTypeWorkflows, defaultWorkflowId, onTransition],
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-3 min-h-[400px] overflow-x-auto pb-2">
        {isMultiWorkflow
          ? categoryColumns.map((col) => {
              const columnItems = items.filter(col.filterFn);
              return (
                <BoardColumn
                  key={col.category}
                  statusId={col.category}
                  label={col.title}
                  color={col.color}
                  count={columnItems.length}
                  activeIssue={activeIssue}
                  isCategory
                  activeCategory={activeIssue?.workflowStatus.category}
                  allowedTargets={allowedTargets}
                >
                  {columnItems.map((issue) => (
                    <DraggableIssueCard
                      key={issue.id}
                      issue={issue}
                      onClick={() => onIssueClick(issue)}
                      showStatusBadge
                    />
                  ))}
                </BoardColumn>
              );
            })
          : statusColumns.map((col) => {
              const columnItems = items.filter(col.filterFn);
              return (
                <BoardColumn
                  key={col.id}
                  statusId={col.id}
                  label={col.title}
                  color={col.color}
                  count={columnItems.length}
                  activeIssue={activeIssue}
                  allowedTargets={allowedTargets}
                >
                  {columnItems.map((issue) => (
                    <DraggableIssueCard
                      key={issue.id}
                      issue={issue}
                      onClick={() => onIssueClick(issue)}
                    />
                  ))}
                </BoardColumn>
              );
            })}
      </div>
      <DragOverlay>
        {activeIssue && <IssueCard issue={activeIssue} overlay showStatusBadge={isMultiWorkflow} />}
      </DragOverlay>
    </DndContext>
  );
}

// --- Board Column (droppable) ---
function BoardColumn({
  statusId,
  label,
  color,
  count,
  children,
  activeIssue,
  isCategory,
  activeCategory,
  allowedTargets,
}: {
  statusId: string;
  label: string;
  color: string;
  count: number;
  children: React.ReactNode;
  activeIssue: IssueListItem | null;
  isCategory?: boolean;
  activeCategory?: string;
  allowedTargets?: Map<string, { isAllowed: boolean; disabledReason: string | null }> | null;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: statusId });

  const isSameColumn = activeIssue
    ? isCategory
      ? activeCategory === statusId
      : activeIssue.statusId === statusId
    : false;

  // CHG-043: determine if this column is allowed/blocked
  let transitionAllowed: boolean | null = null; // null = no transition data (free mode or no data)
  if (activeIssue && !isSameColumn && allowedTargets) {
    const target = allowedTargets.get(statusId);
    transitionAllowed = target?.isAllowed ?? false;
  }

  const isValidTarget = activeIssue && !isSameColumn;
  const isBlocked = transitionAllowed === false;

  return (
    <div
      ref={setNodeRef}
      className={`rounded-lg border-t-4 p-3 transition-all min-w-[200px] flex-1 relative ${
        isOver && isValidTarget && !isBlocked
          ? 'ring-2 ring-primary-500 bg-primary-50/50'
          : 'bg-gray-50'
      } ${isOver && isBlocked ? 'ring-2 ring-red-300 bg-red-50/30' : ''} ${
        isOver && !isValidTarget && activeIssue ? 'ring-2 ring-red-300 bg-red-50/30' : ''
      } ${activeIssue && !isSameColumn && isBlocked ? 'opacity-50' : ''}`}
      style={{ borderTopColor: color }}
    >
      {/* Blocked overlay during drag */}
      {activeIssue && !isSameColumn && isBlocked && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-200/40 rounded-lg pointer-events-none">
          <Ban className="size-8 text-gray-400" />
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">{label}</h3>
        <span className="text-sm text-gray-500 bg-white/70 px-1.5 py-0.5 rounded">
          {count}
        </span>
      </div>
      <div className="space-y-2 min-h-[100px]">
        {children}
      </div>
    </div>
  );
}

// --- Draggable card wrapper ---
function DraggableIssueCard({
  issue,
  onClick,
  showStatusBadge,
}: {
  issue: IssueListItem;
  onClick: () => void;
  showStatusBadge?: boolean;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: issue.id,
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={isDragging ? 'opacity-30' : ''}
    >
      <IssueCard issue={issue} onClick={onClick} showStatusBadge={showStatusBadge} />
    </div>
  );
}

// --- Issue card ---
function IssueCard({
  issue,
  overlay,
  onClick,
  showStatusBadge,
}: {
  issue: IssueListItem;
  overlay?: boolean;
  onClick?: () => void;
  showStatusBadge?: boolean;
}) {
  const { openProfileModal } = useProfileModal();
  const priority = PRIORITY_BADGE[issue.priority];

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      className={`bg-white border border-gray-200 rounded-lg p-3 cursor-pointer hover:shadow-md transition-shadow ${
        overlay ? 'shadow-xl rotate-2' : ''
      }`}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-mono text-gray-400">{issue.issueId}</span>
        {(() => {
          const cfg = resolveIssueTypeConfig(issue.issueTypeDef, issue.issueType);
          return <cfg.Icon size={14} className={cfg.color} style={cfg.hexColor ? { color: cfg.hexColor } : undefined} />;
        })()}
      </div>
      <div className="text-sm font-medium text-gray-900 line-clamp-2 mb-2">
        {issue.title}
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className={`px-1.5 py-0.5 text-[11px] font-medium rounded ${priority?.class ?? ''}`}>
            {priority?.label ?? issue.priority}
          </span>
          {showStatusBadge && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium rounded text-white"
              style={{ backgroundColor: issue.workflowStatus.color }}
            >
              {issue.workflowStatus.name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {issue._count.comments > 0 && (
            <span className="flex items-center gap-0.5 text-sm text-gray-400">
              <MessageSquare size={10} />
              {issue._count.comments}
            </span>
          )}
          {issue.assignee ? (
            <span onClick={(e) => e.stopPropagation()}>
              <Avatar
                size="xs"
                userId={issue.assignee.id}
                name={issue.assignee.name}
                avatarUrl={issue.assignee.avatarUrl}
                displayColor={issue.assignee.displayColor as ProfileColorKey | null | undefined}
                firstName={issue.assignee.firstName}
                onClick={() => openProfileModal(issue.assignee!.id)}
              />
            </span>
          ) : (
            <div className="w-5 h-5 rounded-full bg-gray-100 text-gray-400 text-[11px] flex items-center justify-center">
              ?
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
